import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ClockAction = 'arrival' | 'departure' | 'break_start' | 'break_end';
export type AuthMethod = 'qr' | 'pin';

export interface OvertimeAlert {
  approaching: boolean;
  hours_worked: number;
  threshold_hours: number;
  overtime_minutes?: number;
}

export interface ClockActionResult {
  success: boolean;
  time_log_id?: string;
  shift_id?: string;
  status_message?: string;
  overtime_alert?: OvertimeAlert;
  error?: string;
}

interface UseClockActionReturn {
  isProcessing: boolean;
  lastResult: ClockActionResult | null;
  error: string | null;
  performAction: (
    businessId: string,
    employeeId: string,
    action: ClockAction,
    authMethod: AuthMethod,
    deviceId?: string
  ) => Promise<ClockActionResult>;
  clearResult: () => void;
}

/**
 * Hook for performing clock actions (arrival, departure, break start/end)
 */
export function useClockAction(): UseClockActionReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<ClockActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const performAction = useCallback(async (
    businessId: string,
    employeeId: string,
    action: ClockAction,
    authMethod: AuthMethod,
    deviceId?: string
  ): Promise<ClockActionResult> => {
    setIsProcessing(true);
    setError(null);

    try {
      // Get device location if available
      let deviceLocation: { lat: number; lng: number } | undefined;
      if ('geolocation' in navigator) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              enableHighAccuracy: false,
            });
          });
          deviceLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        } catch {
          // Geolocation not available or denied - continue without it
        }
      }

      const { data, error: fnError } = await supabase.functions.invoke('kiosk-clock-action', {
        body: {
          business_id: businessId,
          employee_id: employeeId,
          action,
          auth_method: authMethod,
          device_id: deviceId,
          device_location: deviceLocation,
        },
      });

      if (fnError) {
        const result: ClockActionResult = {
          success: false,
          error: fnError.message || 'Action failed',
        };
        setLastResult(result);
        setError(result.error || null);
        setIsProcessing(false);
        return result;
      }

      const result: ClockActionResult = {
        success: data.success,
        time_log_id: data.time_log_id,
        shift_id: data.shift_id,
        status_message: data.status_message,
        overtime_alert: data.overtime_alert,
        error: data.error,
      };

      setLastResult(result);
      if (!result.success) {
        setError(result.error || 'Action failed');
      }
      setIsProcessing(false);
      return result;

    } catch (err) {
      console.error('Clock action error:', err);
      const result: ClockActionResult = {
        success: false,
        error: 'Failed to perform action. Please try again.',
      };
      setLastResult(result);
      setError(result.error || null);
      setIsProcessing(false);
      return result;
    }
  }, []);

  const clearResult = useCallback(() => {
    setLastResult(null);
    setError(null);
  }, []);

  return {
    isProcessing,
    lastResult,
    error,
    performAction,
    clearResult,
  };
}

/**
 * Get human-readable action labels
 */
export function getActionLabel(action: ClockAction): string {
  switch (action) {
    case 'arrival':
      return 'Clock In';
    case 'departure':
      return 'Clock Out';
    case 'break_start':
      return 'Start Break';
    case 'break_end':
      return 'End Break';
    default:
      return action;
  }
}

/**
 * Get action icon name (for Lucide icons)
 */
export function getActionIcon(action: ClockAction): string {
  switch (action) {
    case 'arrival':
      return 'LogIn';
    case 'departure':
      return 'LogOut';
    case 'break_start':
      return 'Coffee';
    case 'break_end':
      return 'Play';
    default:
      return 'Clock';
  }
}
